import { useMemo } from "react";
import { MetricCardsContainer } from "@/components/metrics/metric-cards-container";
import type { MetricCardConfig } from "@/components/metrics/types";
import type { CampaignMetrics } from "@/hooks/api/use-campaign-metrics";
import { useCampaignFilterState } from "@/hooks/use-campaign-filter-state";
import { useMetricCardFilters } from "@/hooks/use-metric-card-filters";
import {
  DEFAULT_METRIC_ORDER,
  HIDDEN_METRICS,
  METRIC_CONFIG,
  WIP_METRICS,
} from "./campaign-metrics-config";

interface CampaignMetricCardsProps {
  metrics: CampaignMetrics | undefined;
  onMetricSelect: (metric: keyof CampaignMetrics) => void;
  loadingStates: Record<
    keyof CampaignMetrics,
    { isLoading: boolean; isError: boolean }
  >;
  benchmarkMetrics?: CampaignMetrics | undefined;
  benchmarkLoadingStates?: Record<
    keyof CampaignMetrics,
    { isLoading: boolean; isError: boolean }
  >;
  showBenchmark?: boolean;
  leftControls?: React.ReactNode;
  showBody?: boolean;
  emptyBody?: React.ReactNode;
}

export function CampaignMetricCards({
  metrics,
  onMetricSelect,
  loadingStates,
  benchmarkMetrics,
  benchmarkLoadingStates,
  showBenchmark = false,
  leftControls,
  showBody,
  emptyBody,
}: CampaignMetricCardsProps) {
  const filterState = useCampaignFilterState("campaigns::explorer");
  const {
    displayFilters,
    filterColumns,
    getTotalFilterCount,
    handleClearFilter,
    clearAllFilters,
  } = useMetricCardFilters(filterState);

  // Convert METRIC_CONFIG to the format expected by MetricCardsContainer
  const convertedMetricConfig = useMemo(() => {
    const config: Record<string, MetricCardConfig> = {};

    Object.entries(METRIC_CONFIG).forEach(([key, value]) => {
      config[key] = {
        title: value.title,
        tooltip: value.tooltip,
        category: value.category,
        render: value.render
          ? (val: number | null, options: Record<string, unknown>) => {
              // Convert generic options to the specific format expected by campaign metrics
              return value.render!(val, {
                metricKey: options.metricKey as keyof CampaignMetrics,
                metrics: options.metrics as CampaignMetrics | undefined,
                benchmarkMetrics: options.benchmarkMetrics as
                  | CampaignMetrics
                  | undefined,
                isBenchmark: options.isBenchmark as boolean | undefined,
              });
            }
          : undefined,
      };
    });

    return config;
  }, []);

  // Default renderMetric that uses the config's render functions
  const renderMetric = (
    value: number | null,
    options: Record<string, unknown>,
  ) => {
    const metricKey = options.metricKey as keyof CampaignMetrics;
    const config = METRIC_CONFIG[metricKey];

    if (config?.render) {
      return config.render(value, {
        metricKey,
        metrics: options.metrics as CampaignMetrics | undefined,
        benchmarkMetrics: options.benchmarkMetrics as
          | CampaignMetrics
          | undefined,
        isBenchmark: options.isBenchmark as boolean | undefined,
      });
    }

    // Fallback - this shouldn't be reached if all configs have render functions
    return value?.toLocaleString() ?? "—";
  };

  return (
    <MetricCardsContainer
      metrics={metrics}
      onMetricSelect={(metric) =>
        onMetricSelect(metric as keyof CampaignMetrics)
      }
      loadingStates={
        loadingStates as Record<
          string,
          { isLoading: boolean; isError: boolean }
        >
      }
      metricConfig={convertedMetricConfig}
      defaultOrder={DEFAULT_METRIC_ORDER as string[]}
      hiddenMetrics={HIDDEN_METRICS as string[]}
      wipMetrics={WIP_METRICS as string[]}
      storageKey="campaigns::explorer::metrics::order"
      title="Top-level campaign metrics"
      entityScope="campaigns::explorer"
      entity="campaign"
      renderMetric={renderMetric}
      benchmarkMetrics={benchmarkMetrics}
      benchmarkLoadingStates={
        benchmarkLoadingStates as Record<
          string,
          { isLoading: boolean; isError: boolean }
        >
      }
      showBenchmark={showBenchmark}
      leftControls={leftControls}
      showBody={showBody}
      emptyBody={emptyBody}
      getTotalFilterCount={getTotalFilterCount}
      onResetMetricFilters={clearAllFilters}
      displayFilters={displayFilters}
      filterColumns={filterColumns}
      clearFilter={handleClearFilter}
    />
  );
}
